<template>
    <section class="mx-16 w-full my-6">
        <div class="bg-white overflow-hidden sm:rounded-lg sm:shadow">
            <div class="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                    {{ t("members.import.title", "Import") }}
                </h3>
                <p class="mt-1 text-sm text-gray-500">
                    {{ t("members.import.text", "Import") }}
                </p>
            </div>

            <div
                class="divide-y divide-gray-200 p-4"
                v-show="results.length == 0"
            >
                <button
                    class="inline-flex items-center px-5 py-2 border border-green-700 rounded-md shadow text-base font-semibold text-green-50 bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    @click="triggerFileUpload('bestand')"
                >
                    <svg
                        class="fill-current w-6 h-6 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                        ></path>
                    </svg>

                    {{
                        t(
                            "members.import.button-excel",
                            "Upload Excel voor import"
                        )
                    }}
                </button>

                <input
                    type="file"
                    id="bestand"
                    accept=".xlsx, .xls"
                    ref="bestand"
                    v-on:change="handleFileUpload('bestand')"
                    style="display: none"
                />
                <empty-list :message="warning" class="mt-4"></empty-list>
            </div>

            <section
                class="divide-y divide-gray-200 px-6 pb-2"
                v-if="results.length > 0"
            >
                <info-box :message="info"></info-box>

                <div class="flex flex-col pt-4 pb-6" v-if="results">
                    <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div
                            class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8"
                        >
                            <table class="min-w-full divide-y divide-gray-300">
                                <thead>
                                    <tr>
                                        <th
                                            scope="col"
                                            class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 md:pl-0"
                                        >
                                            {{
                                                t(
                                                    "members.import.th-column",
                                                    "Kolom"
                                                )
                                            }}
                                        </th>
                                        <th
                                            scope="col"
                                            class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 md:pl-0"
                                        >
                                            {{
                                                t(
                                                    "members.import.excel-column-first",
                                                    "Kopregel"
                                                )
                                            }}
                                        </th>
                                        <th
                                            scope="col"
                                            class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 md:pl-0"
                                            v-for="i in length"
                                            :key="i"
                                        >
                                            {{
                                                t(
                                                    "members.import.th-row",
                                                    "Rij"
                                                )
                                            }}
                                            {{ i }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-gray-200">
                                    <tr v-for="(row, r) in rows" :key="r">
                                        <td
                                            class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 md:pl-0"
                                        >
                                            <select
                                                v-model="rowpicker[r]"
                                                class="mt-1 block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                            >
                                                <option
                                                    v-for="(
                                                        option, o
                                                    ) in options"
                                                    :key="o"
                                                    :value="o"
                                                >
                                                    {{ option }}
                                                </option>
                                            </select>
                                        </td>
                                        <td
                                            class="whitespace-nowrap py-4 px-3 text-sm text-gray-500 border"
                                        >
                                            <b>{{ row }}</b>
                                        </td>
                                        <td
                                            class="whitespace-nowrap py-4 px-3 text-sm text-gray-500 border"
                                            v-for="i in length"
                                            :key="i"
                                        >
                                            <span v-if="results[i - 1]">
                                                {{ results[i - 1][row] }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <button
                    v-if="rowpicker.length > 0"
                    class="inline-flex items-center px-5 py-2 border border-green-700 rounded-md shadow text-base font-semibold text-green-50 bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 mb-4"
                    @click="handleImport()"
                >
                    {{ t("members.import.start-excel", "Start import") }}
                </button>
            </section>
        </div>
    </section>
</template>

<script>
import { InformationCircleIcon } from "@heroicons/vue/solid";
import InfoBox from "../../../components/InfoBox.vue";
import EmptyList from "../../../components/EmptyList.vue";
import Swal from "sweetalert2";

export default {
    components: {
        InformationCircleIcon,
        InfoBox,
        EmptyList,
    },
    mounted() {
        this.info =
            "<b>" +
            this.t("members.selection-import.info.title", "Instructies") +
            "</b><br>" +
            this.t(
                "members.selection-import.info.text",
                "Informatie hierover."
            );

        this.warning =
            "<b>" +
            this.t(
                "members.import.warning.title",
                "Let op het volgende bij het importeren:"
            ) +
            "</b><br>" +
            this.t(
                "members.import.warning.text",
                "• E-mailadressen moeten uniek zijn en mogen maar één keer voorkomen.<br>• Een mobiel telefoonnummer is noodzakelijk om in te kunnen loggen met SMS-verificatie."
            );
    },
    created() {},
    data() {
        return {
            info: null,
            rowpicker: [],
            warning: null,
            length: 0,
            options: {
                "": null,
                name: "Naam",
                lastname: "Achternaam",
                email: "E-mailadres",
                phone: "Telefoonnummer",
                voting_weight: "Stemgewicht",
                // birthday: "Geboortedatum",
                // gender: "Geslacht",
                street: "Straatnaam",
                housenumber: "Huisnummer",
                // address: "Adres",
                postcode: "Postcode",
                city: "Stad",
                country: "Land",
                consumerAccount: "IBAN",
                consumerName: "Rekeninghouder",
                ice_name: "ICE-naam",
                ice_phone: "ICE-telefoonnummer",
            },
            rows: [],
            results: [],
        };
    },
    methods: {
        triggerFileUpload(field) {
            document.getElementById(field).click();
        },
        handleFileUpload(field) {
            this.file = this.$refs.bestand.files[0];
            var self = this;
            let formData = new FormData();
            formData.append("bestand", this.file);
            formData.append("custom", true);
            window.scroll({
                top: 0,
                left: 0,
                behavior: "smooth",
            });
            axios
                .post("/api/import-users", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then(function (response) {
                    console.info(response.data);
                    if (response.data) {
                        self.results = response.data.data;
                        self.rows = response.data.rows;
                        self.length = 3; //Object.values(self.results[0]).length - 1;
                    }
                })
                .catch((error) => {
                    //console.error(error);
                    if (error.response) {
                        // if (error.response.status == 403) {
                        //     alert("Geen toegang hiervoor");
                        // }
                        // if (error.response.status == 404) {
                        //     alert("Niet (meer) beschikbaar");
                        // }
                        // if (error.response.status == 422) {
                        //     alert("Bestand niet van het formaat Excel");
                        // }
                        if (error.response.status == 409) {
                            Swal.fire(
                                self.t(
                                    "members.import.failed-title",
                                    "Import mislukt"
                                ),
                                self.t(
                                    "members.import.excel-incorrect",
                                    "Template niet correct gevuld en/of uw Excel-sheet is voorzien van een wachtwoord."
                                ),
                                "error"
                            );
                        }
                        if (error.response.status == 500) {
                            Swal.fire(
                                self.t(
                                    "members.import.failed-title",
                                    "Import mislukt"
                                ),
                                self.t(
                                    "members.import.file-failure",
                                    "Er is wat mis met de aangeleverd Excel. Probeer het nog eens."
                                ),
                                "error"
                            );
                            setTimeout(() => {
                                window.location.reload();
                            }, 1000);
                        }
                        // if (error.response.status == 500) {
                        //     // alert("Hier ging wat mis op de server.\nProbeer het nog eens.");
                        // }
                    }
                })
                .finally(function () {});
        },
        handleImport() {
            this.file = this.$refs.bestand.files[0];
            var self = this;
            let formData = new FormData();
            formData.append("bestand", this.file);
            formData.append("custom", true);
            formData.append("rowpicker", this.rowpicker);
            formData.append("rows", this.rows);

            window.scroll({
                top: 0,
                left: 0,
                behavior: "smooth",
            });
            axios
                .post("/api/import-users", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then(function (response) {
                    console.info(response.data);
                    if (response.data) {
                        Eventing.fire("notify", {
                            title: self.t("members.import.success", "Gelukt"),
                            message: self.t(
                                "members.import.success-text",
                                "Bestand wordt geïmporteerd"
                            ),
                            type: "alert",
                            position: "center",
                        });
                        self.$router.replace({ name: "members" });
                    }
                })
                .catch((error) => {
                    //console.error(error);
                    if (error.response) {
                        if (error.response.status == 409) {
                            Swal.fire(
                                self.t(
                                    "members.import.failed-title",
                                    "Import mislukt"
                                ),
                                self.t(
                                    "members.import.excel-incorrect",
                                    "Template niet correct gevuld en/of uw Excel-sheet is voorzien van een wachtwoord."
                                ),
                                "error"
                            );
                        }
                        if (error.response.status == 500) {
                            Swal.fire(
                                self.t(
                                    "members.import.failed-title",
                                    "Import mislukt"
                                ),
                                self.t(
                                    "members.import.file-failure",
                                    "Er is wat mis met de aangeleverd Excel. Probeer het nog eens."
                                ),
                                "error"
                            );
                            setTimeout(() => {
                                window.location.reload();
                            }, 1000);
                        }
                        // if (error.response.status == 500) {
                        //     // alert("Hier ging wat mis op de server.\nProbeer het nog eens.");
                        // }
                    }
                })
                .finally(function () {});
        },
    },
};
</script>
